import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { usePaymentMethodProviders } from "Payments/globalPaymentsStateHooks";

import API from "./API";
import AddPaymentMethodButton from "./AddPaymentMethodButton";
import AddPaymentMethodModal from "./AddPaymentMethodModal";
import {
  ManagePaymentMethodsProvider,
  useManagePaymentMethods,
} from "./ManagePaymentMethodsContext";
import NoPaymentMethodsPlaceholder from "./NoPaymentMethodsPlaceholder";
import PaymentMethodsList from "./PaymentMethodsList";
import WePayACHPlaidModal from "./WePayACHPlaidModal";

// PROP TYPES
ManagePaymentMethods.propTypes = {
  showQuickControls: PropTypes.bool,
  newCardMustBeSaved: PropTypes.bool,
  onCardSelect: PropTypes.func,
  show: PropTypes.bool,
  networkName: PropTypes.string,
  tabID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  userID: PropTypes.number,
  initialPaymentMethods: PropTypes.array,
  initialUserData: PropTypes.object,
  updateExternalPaymentMethods: PropTypes.func,
  forceIsACHAllowed: PropTypes.bool,
  forceIsCardAllowed: PropTypes.bool,
};

// MAIN COMPONENT (Not Exported)
function ManagePaymentMethods({
  showQuickControls,
  onCardSelect,
  newCardMustBeSaved,
  initialPaymentMethods,
  initialUserData,
  userID,
  updateExternalPaymentMethods,
  show = true,
  tabID,
  networkName,
  forceIsACHAllowed,
  forceIsCardAllowed,
}) {
  const { state, dispatch } = useManagePaymentMethods();
  const { paymentMethods, user, isCardAllowed, isACHAllowed } = state;
  const { hasMobilePhone, otherHoHNames, userID: retrievedUserID } = user;
  const userIDToUse = userID || retrievedUserID;
  const { setPaymentMethodProviders } = usePaymentMethodProviders();

  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] =
    useState(false);
  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [initialPaymentType, setInitialPaymentType] = useState();

  // this value is undefined in wallet area, but defined on payment page → manage PMs screen
  const isCardAllowedFinal =
    forceIsCardAllowed !== undefined ? forceIsCardAllowed : isCardAllowed;

  // this value is undefined in wallet area, but defined on payment page → manage PMs screen
  const isACHAllowedFinal =
    forceIsACHAllowed !== undefined ? forceIsACHAllowed : isACHAllowed;

  // Fetch Payment Methods and Save to State
  useEffect(() => {
    if (!initialPaymentMethods && paymentMethods === null) {
      API.getSavedPaymentMethods(userIDToUse)
        .post()
        .then((resp) => {
          dispatch({
            type: "Set Payment Methods And User",
            payload: {
              paymentMethods: resp.paymentMethods.sort((method) =>
                method.isDefaultPaymentMethod ? -1 : 0
              ),
              user: resp.user,
              isCardAllowed: resp.isCardAllowed,
              isACHAllowed: resp.isACHAllowed,
            },
          });

          setPaymentMethodProviders(resp?.paymentMethodProviders);
          // setPaymentMethodProviders([
          //   {
          //     paymentMethodCategory: "credit_card",
          //     paymentProvider: 2,
          //     merchantToken: "Test-Merchant-23f1eba98b9f",
          //     paymentProviderName: "Stax",
          //   },
          //   {
          //     paymentMethodCategory: "bank_account",
          //     paymentProvider: 2,
          //     merchantToken: "Test-Merchant-23f1eba98b9f",
          //     paymentProviderName: "Stax",
          //   },
          // ]);
        });
    }
  }, [
    userIDToUse,
    paymentMethods,
    dispatch,
    initialPaymentMethods,
    setPaymentMethodProviders,
  ]);

  useEffect(() => {
    if (initialPaymentMethods) setPaymentMethods(initialPaymentMethods, false);
  }, [initialPaymentMethods]);

  useEffect(() => {
    if (initialUserData && !user?.userID)
      dispatch({
        type: "Set User",
        payload: initialUserData,
      });
  }, [initialUserData, user.userID, dispatch]);

  const setPaymentMethods = (
    paymentMethods = [],
    shouldUpdateExternal = true
  ) => {
    dispatch({
      type: "Set Payment Methods",
      payload: paymentMethods.sort((PM) =>
        PM.isDefaultPaymentMethod ? -1 : 0
      ),
    });
    if (shouldUpdateExternal && updateExternalPaymentMethods)
      updateExternalPaymentMethods(paymentMethods);
  };

  if (!paymentMethods) return null;

  return (
    <>
      <NoPaymentMethodsPlaceholder />
      <PaymentMethodsList
        showQuickControls={showQuickControls}
        hasMobilePhone={hasMobilePhone}
        onCardSelect={onCardSelect}
        show={show}
        updateExternalPaymentMethods={updateExternalPaymentMethods}
        networkName={networkName}
      />
      <AddPaymentMethodButton
        setShowModal={setShowAddPaymentMethodModal}
        setShowPlaidModal={setShowPlaidModal}
        isCardAllowed={isCardAllowedFinal}
        isACHAllowed={isACHAllowedFinal}
        setInitialPaymentType={setInitialPaymentType}
      />
      <AddPaymentMethodModal
        show={showAddPaymentMethodModal}
        setShow={setShowAddPaymentMethodModal}
        setPaymentMethods={setPaymentMethods}
        mustSaveCard={newCardMustBeSaved}
        userID={userIDToUse}
        hasMobilePhone={hasMobilePhone}
        hasOtherPaymentMethods={paymentMethods?.length > 0}
        sharableWith={otherHoHNames}
        existingUserName={user.name}
        existingZip={user.zip}
        tabID={tabID}
        networkName={networkName}
        initialPaymentType={initialPaymentType}
      />
      <WePayACHPlaidModal
        show={showPlaidModal}
        setShow={setShowPlaidModal}
        onSave={(res) => setPaymentMethods(res.paymentMethods)}
        shouldSavePaymentMethod
        userID={userIDToUse}
      />
    </>
  );
}

// EXPORTED COMPONENT WITH PROVIDER
export default function ManagePaymentMethodsWithProvider(props) {
  return (
    <ManagePaymentMethodsProvider>
      <ManagePaymentMethods {...props} />
    </ManagePaymentMethodsProvider>
  );
}
